import { useRouter } from "next/router";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { meta } from "./_meta";
import Modal from "@/components/modal";
import Button from "@/components/button";
import { LayoutContext } from "@/components/layout-context-container";
import styles from './_hcp-clinical-information.module.scss';
import HcpHero from "@/components-hcp/hero";
import HcpLongButton from "@/components-hcp/long-button";
import HcpPageFootnote from "@/components-hcp/page-footnote";
import HcpClinicalInformationTabAndToggleNavigation from "./components/tab-and-toggle-navigation";
import HcpClinicalInformationDoseReduction from "./components/dose-reduction";
import HcpClinicalInformationBaseline from "./components/baseline";
import HcpClinicalInformationConsider from "./components/consider";
import HcpClinicalInformationAndrostenedione from "./components/androstenedione";
import HcpClinicalInformationAndrogenRelated from "./components/androgen-related";
import HcpClinicalInformationDoseReductions from "./components/dose-reductions";
import HcpClinicalInformationReducedGc from "./components/reduced-gc";
import HcpClinicalInformationHydrocortisoneDoses from "./components/hydrocortisone-doses";
import HcpClinicalInformationMetabolicHealth from "./components/metabolic-health";
import HcpClinicalInformationBMIAndMetabolicHealthData from "./components/bmi-and-metabolic-health-data";
import HcpClinicalInformationSafety from "./components/safety";
import HcpClinicalInformationCompletionRates from "./components/completion-rates";
import HcpClinicalInformationDiscontinuation from "./components/discontinuation";
// import HcpClinicalInformationActh from "./components/acth";
import HcpClinicalInformationPhysiologic from "./components/physiologic";
import HcpClinicalInformationGdReductionCards from "./components/gc-reduction-cards";
import HcpClinicalInformationMajority from "./components/majority";
import HcpPageReferences from "@/components-hcp/page-references";
import HcpAccessSimplified from "../dosing/access-simplified";
import HcpClinicalInformationVideo from "./components/video";
import HcpDrQuote from "@/components-hcp/dr-quote";
import HcpClinicalInformationPatientSatisfactionModal from "./components/patient-satisfaction-modal";

export default function HcpClinicalInformation() {
  const { setFooterOverrideEndMatter } = useContext(LayoutContext);
  const [tabIndex, setTabIndex] = useState(0);
  const [toggleIndex, setToggleindex] = useState(0);
  const { routerIsReady } = useContext(LayoutContext);
  const router = useRouter();
  const patientSatisfactionModalRef = useRef(null);
  
  const references = useMemo(() => [
    <>Crenessity. Package insert. Neurocrine Biosciences, Inc.</>,
    <>Neurocrine Biosciences announces FDA approval of CRENESSITY (crinecerfont), a first-in-class treatment for children and adults with classic congenital adrenal hyperplasia. News release. Neurocrine Biosciences. December 13, 2024. Accessed August 7, 2025. https://neurocrine.gcs-web.com/news-releases/news-release-details/neurocrine-biosciences-announces-fda-approval-crenessitytm</>,
    <>Jeha GS, Newfield RS, Geffner ME, et al. Crinecerfont maintains reductions in serum androstenedione levels and glucocorticoid doses in children and adolescents with classic congenital adrenal hyperplasia: 1-year results from the CAHtalyst™ Pediatric Study. Presented at: Pediatric Endocrine Society Annual Meeting (PES 2025); April 30-May 3, 2025; San Francisco, CA.</>,
    <>Vaid S, Merke DP, Salam M, et al. Crinecerfont improves reproductive hormones in classic congenital adrenal hyperplasia: 1-year results from the CAHtalyst™ Adult Study. Poster presented at: American Association of Clinical Endocrinology Annual Meeting; May 15-17, 2025; Orlando, FL.</>,
    <>Nokoff NJ, Kim MS, Manousaki D, et al. Long-term crinecerfont treatment reduced adrenocorticotropic hormone and 17-hydroxyprogesterone and improved clinical outcomes in children and adolescents with classic congenital adrenal hyperplasia: 2-year results from CAHtalyst&#174; Pediatric. Poster presented at: Pediatric Endocrine Society Annual Meeting; April 30-May 3, 2026; <span className="text-nowrap">San Francisco, CA.</span></>,
    <>Sarafoglou K, Bouvattier C, Capalbo D, et al. Long-term crinecerfont treatment enables sustained decreases in glucocorticoid doses and improves clinical outcomes in children and adolescents with classic congenital adrenal hyperplasia: 2-year results from CAHtalyst&#174; Pediatric. Poster presented at: The Endocrine Society. June 13-16, 2026; Chicago, IL.</>,
    <>Bancos I, Hamidi O, Kiefer FW, et al. Adults with classic congenital adrenal hyperplasia taking crinecerfont demonstrated sustained decreases in glucocorticoid doses: 2-year results from the CAHtalyst<sup>&reg;</sup> Adult Study. Presented at: The Endocrine Society Annual Meeting. June 13-16, 2026; Chicago, IL.</>,
    <>Data on file, Neurocrine Biosciences, Inc. </>,
    <>Sarafoglou K, Kim MS, Lodish M, et al. Phase 3 trial of crinecerfont in pediatric congenital adrenal hyperplasia. <em>N Engl J Med.</em> 2024;391(6):493-503. doi:10.1056/NEJMoa2404655</>,
    <>Auchus RJ, Hamidi O, Pivonello R, et al. Phase 3 trial of crinecerfont in adult congenital adrenal hyperplasia. <em>N Engl J Med.</em> 2024;391(6):504-514. doi:10.1056/NEJMoa2404656</>,
    <>Sarafoglou K, Kim MS, Lodish M, et al. Phase 3 trial of crinecerfont in pediatric congenital adrenal hyperplasia. Supplementary protocol. <em>N Engl J Med.</em> 2024;391(6):493-503. doi:10.1056/NEJMoa2404655</>,
    <>Sarafoglou K, Kim MS, Lodish M, et al. Phase 3 trial of crinecerfont in pediatric congenital adrenal hyperplasia. Supplementary appendix. <em>N Engl J Med</em>. 2024;391(6):493-503. doi:10.1056/NEJMoa2404655</>,
    <>Sarafoglou K, Lekarev O, Loechner K, et al. Crinecerfont shows favorable trends in improving clinical outcomes in children and adolescents with classic congenital adrenal hyperplasia: 1-year results from the CAHtalyst™ Pediatric Study. Poster presented at: Pediatric Endocrine Society Annual Meeting; May 15-18, 2025; National Harbor, MD.</>,
    <>Vaid S, Abramowicz J, Bancos I, et al. A cross-sectional survey on crinecerfont and quality of life for patients with classic congenital adrenal hyperplasia in the United States participating in CAHtalyst&#174; adult and pediatric open-label extension. Presented at: The Endocrine Society Annual Meeting; June 13-16, 2026; <span className="text-nowrap">Chicago, IL.</span></>,
    <>Merke DP, Auchus RJ. Congenital adrenal hyperplasia due to 21-hydroxylase deficiency. <em>N Engl J Med.</em> 2020;383(13):1248-1261. doi:10.1056/NEJMra1909786</>,
    <>Prete A, Auchus RJ, Ross RJ. Clinical advances in the pharmacotherapy of congenital adrenal hyperplasia. <em>Eur J Endocrinol.</em> 2021;186(1):R1-R14. doi:10.1530/EJE-21-0794</>,
    <>Hindmarsh PC, Geertsma K. <em>Congenital Adrenal Hyperplasia: A Comprehensive Guide.</em> Elsevier/Academic Press; 2017.</>,
    <>Sarafoglou K, Merke DP, Reisch N, Claahsen-van der Grinten H, Falhammar H, Auchus RJ. Interpretation of steroid biomarkers in 21-hydroxylase deficiency and their use in disease management. <em>J Clin Endocrinol Metab.</em> <span>2023;108(9):2154-2175.</span> doi:10.1210/clinem/dgad1345</>,
    <>Koren R, Koren S, Khashper A, Benbassat C, Pekar-Zlotin M, Vaknin Z. Ovarian adrenal rest tumor in congenital adrenal hyperplasia: is medical treatment the first line option? <em>Arch Endocrinol Metab.</em> 2021;65(6):841-884. doi:10.20945/2359-3997000000415</>,
    <>Speiser PW, Arlt W, Auchus RJ, et al. Congenital adrenal hyperplasia due to steroid 21-hydroxylase deficiency: an Endocrine Society clinical practice guideline. <em>J Clin Endocrinol Metab.</em> <span>2018;103(11):4043-4088.</span> doi:10.1210/jc.2018-01865</>,
    <>Bancos I, Kim H, Cheng HK, et al. Glucocorticoid therapy in classic congenital adrenal hyperplasia: traditional and new treatment paradigms. <em>Expert Rev Endocrinol Metab</em>. 2025;20(1):33-49. doi:10.1080/17446651.2025.2450423</>,
    <>Lekarev O, Erkin-Cakmak A, Vogiatzi MG, et al. Crinecerfont enables reduction of glucocorticoid doses while maintaining or improving androstenedione in pediatric patients with classic congenital adrenal hyperplasia: subgroup analyses from the CAHtalyst™ Pediatric Phase 3 Study. Poster presented at: Pediatric Endocrine Society Annual Meeting; May 15-18, 2025; National Harbor, MD.</>,
    <>Neurocrine Biosciences. Presented at J.P. Morgan Healthcare Conference; January 12, 2026; <span className="text-nowrap">San Francisco, CA.</span></>,
    <>Vogiatzi MG, Cools M, Coutant R, et al. Characterization of children and adolescents with classic CAH who had slowed bone age progression and improved height prediction with crinecerfont. Presented at: The Endocrine Society Annual Meeting; June 14, 2026; <span className="text-nowrap">Chicago, IL.</span></>,
    <>Auchus RJ, Hamidi O, Pivonello R, et al. Phase 3 trial of crinecerfont in adult congenital adrenal hyperplasia. Supplementary appendix. <em>N Engl J Med</em>. 2024;391(6):504-514. doi:10.1056/NEJMoa2404656</>,
    <>Auchus RJ, Hamidi O, Bancos I, et al. Crinecerfont improves clinical outcomes in adults with classic congenital adrenal hyperplasia: 1-year results from the CAHtalyst™ Adult Study. Poster presented at: Endocrine Society Annual Meeting; July 12-15, 2025; <span className="text-nowrap">San Francisco, CA.</span></>,
    <>Auchus RJ, Hamidi O, Pivonello R, et al. Phase 3 trial of crinecerfont in adult congenital adrenal hyperplasia. Protocol. <em>N Engl J Med</em>. 2024;391(6):504-514. doi:10.1056/NEJMoa2404656</>,
    <>Hamidi 0, Chalmers LJ, Falhammar H, et al. Crinecerfont improves weight-related outcomes and insulin resistance in adults with classic congenital adrenal hyperplasia: 2-year results from the CAHtalyst® adult study. Presented at: The Endocrine Society Annual Meeting. June 13-16, 2026; Chicago, IL.</>,
  ], []);

  const transcript = useMemo(() => (
    <div>
      <p><strong>Length: 03:50</strong></p>
      <p><strong>HEIDI SHEA, MD:</strong> In any of our visits, I think all of us sort of get an idea of, &ldquo;What are the concerns of the patient? What are their goals of treatment?&rdquo;</p>
      <p>So when we start CRENESSITY, we also bring that in and talk about the fact of you&rsquo;re worried about your bones, you&rsquo;re worried about your weight, you&rsquo;re worried about your glucose intolerance with high dose steroids. So with this drug, we&rsquo;re going to see how we&rsquo;re able to keep you in a more physiologic manner of dosing, and then we&rsquo;re also going to see how these other things end up improving for you.</p>
      <p><strong>AMRIT BHANGOO, MD:</strong>  So when I talk about CRENESSITY for the first time with a patient if they have not heard about it before…I do start off by just talking about physiology a little bit more in the language that they can understand the best…And then go into what CAH does, what happens in CAH, what ACTH hormone&rsquo;s role is, and adrenal hormones.</p>
      <p>Then I bring the discussion about CRENESSITY and how it suppresses the ACTH level and…adrenal androgen. If they&rsquo;re a little hesitant, I would show them their previous lab results, in which they were like higher levels of androstenedione and 17-hydroxyprogesterone.</p>
      <p>I will tell them that, &ldquo;I&rsquo;ve been very conservative with your hydrocortisone dose, but you are actually on a higher glucocorticoid dose than is required.&rdquo;</p>
      <p>And then give them some reading material about the trial, reassure them that the trial has been done, and it&rsquo;s been approved by the FDA.</p>
      <p><strong>RUN YU, MD/PHD:</strong>  If their adrenal androgens are really high…or they are on a supraphysiological dose… then I would say, you know, it may be worthwhile to add another pill twice a day, and you can maybe bundle that pill with your hydrocortisone.</p>
      <p><strong>HEIDI SHEA, MD:</strong>  If they&rsquo;re coming in from a pediatric setting, a lot of these patients may not have had a chance to discuss CRENESSITY with their prior provider or pediatric endocrinologist. So, I like to think about it in a couple visits.</p>
      <p>The first visit with a new patient is really to establish a rapport, ….Do they have an action plan for their adrenal insufficiency? What are their goals of treatment, now that they&rsquo;ve transitioned? So, we get those things out of the way, and then the second visit, you might introduce a medication that they can possibly use.</p>
      <p><strong>HEIDI SHEA, MD:</strong> When starting CRENESSITY, Neurocrine Access Support program really has been helpful. It does seem like the program is streamlined.</p>
      <p>The patients are getting on CRENESSITY very quickly. We also have a representative that is a liaison to my staff. So she keeps us updated on…where the status is of the patient, if there&rsquo;s any issues, or even if the patient hasn&rsquo;t called the pharmacy, when they&rsquo;re trying to reach them, which sometimes happens. So, I would say the Neurocrine Access program really has gotten into a good groove, and it is really a resource to my staff as well, as to the patients.</p>
      <p><strong>RUN YU, MD/PHD:</strong>  So, my experience with the Neurocrine Access Support program has been pretty positive. CRENESSITY is in our formulary now. So, I can order it on our medical record system, and we still need to do a few, you know, prior authorizations still, but overall so far, my patient has not had any access issue.</p>
      <p><strong>AMRIT BHANGOO, MD:</strong>  I would agree with both Dr. Yu and Dr. Shea that my experience with the Neurocrine Access Support program has been really good. After prescribing the medication in our EMR, I fill out that form and I get our clinical staff involved in it ...and in two patients I got the response back within one week, that the medication is approved. So it has been a good response.</p>
    </div>
  ), []);

  useEffect(() => {
    const handleHashChange = () => {
      if (routerIsReady) {
        const hash = window.location.hash;
        if (hash.replace('#', '') === 'trial-1') {
          setTabIndex(0);
          setToggleindex(0);
          // window.scrollTo({top: 0, behavior: 'instant'});
        } else if (hash.replace('#', '') === 'trial-2') {
          setTabIndex(1);
          setToggleindex(0);
          // window.scrollTo({top: 0, behavior: 'instant'});
        } else if (hash.replace('#', '').startsWith('trial-1-efficacy') || hash.replace('#', '').startsWith('trial-1-efficacy-link')) {
          setTabIndex(0);
          setToggleindex(1);
        } else if (hash.replace('#', '').startsWith('trial-1-safety') || hash.replace('#', '').startsWith('trial-1-safety-link')) {
          setTabIndex(0);
          setToggleindex(2);
        } else if (hash.replace('#', '').startsWith('trial-2-efficacy')) {
          setTabIndex(1);
          setToggleindex(1);
        } else if (hash.replace('#', '').startsWith('trial-2-safety') || hash.replace('#', '').startsWith('trial-2-safety-link')) {
          setTabIndex(1);
          setToggleindex(2);
        }
      }
    };

    routerIsReady && handleHashChange();
    
    window.addEventListener('hashchange', handleHashChange);

    return () => window.removeEventListener('hashchange', handleHashChange);

  }, [routerIsReady, router]);

  useEffect(() => {
    if (tabIndex === 1 && toggleIndex === 1) {
      setFooterOverrideEndMatter({
        codeDate: <><span className="text-nowrap">CP-CFT-US-0263v5</span>&nbsp; <span>08/2026</span>&nbsp; <span className="text-nowrap">CP-CFT-US-1235</span>&nbsp; <span>09/2026</span></>,
        code: '',
        codeYear: '2026',
      });
    } else {
      setFooterOverrideEndMatter(null);
    }
    return () => {
      setFooterOverrideEndMatter(null);
    };
  }, [tabIndex, toggleIndex, setFooterOverrideEndMatter]);

  /* useEffect(() => {
    if (window.location.hash) {
      window.history.replaceState(null, null, window.location.pathname + window.location.search);
    
      // Option 2: Push a new state to the history (creates a new entry)
      // window.history.pushState("", document.title, window.location.pathname + window.location.search);
    }
  }, [tabIndex, toggleIndex]) */

  return (
    <>
      <div className={styles.container}>
        <div className={`container ${styles.inner_container}`}>
          <p className={styles.pre_heading}>
            The efficacy and safety of CRENESSITY were evaluated in the largest and longest-ever clinical trial program for a classic CAH <span className="text-nowrap">treatment.<sup>1-7</sup></span>
          </p>
          <p className={styles.sub_heading}>
            This most extensive patient cohort to date included 285 pediatric and adult patients, with open-label treatment for 233 patients extending up to 2 years and beyond for most.<sup>1,6-8</sup>
          </p>

          {/* add link here */}
          <div className={styles.info_link_row}>
            <Button
              type="button"
              customClass={styles.info_link}
              onClick={() => patientSatisfactionModalRef.current?.open()}
              data-gtm-event-category="Action"
              data-gtm-event-action="Click"
              data-gtm-event-label="Explore patient satisfaction with CRENESSITY"
            >
              <span  className={styles.info_link_text}>Explore patient satisfaction with CRENESSITY</span><sup className={styles.info_icon}>ⓘ</sup>
              
            </Button>
          </div>
        </div>
        <HcpClinicalInformationGdReductionCards tabIndex={tabIndex}/>
        <HcpClinicalInformationTabAndToggleNavigation setTabIndex={setTabIndex} setToggleindex={setToggleindex} tabIndex={tabIndex} toggleIndex={toggleIndex} />
        {toggleIndex === 0 && <>
          <HcpClinicalInformationDoseReduction index={tabIndex} />
          <HcpClinicalInformationBaseline index={tabIndex} />
        </>}
        {toggleIndex === 1 && <>
          <HcpClinicalInformationConsider index={tabIndex} />
          <HcpClinicalInformationAndrostenedione index={tabIndex} />
          {tabIndex === 0 && <>
            <HcpDrQuote
              theme="lilac"
              doctor={null}
            >
              Reducing my steroid dose helped...me feel more confident about my health.”<span className="font-extralight"> — Participant 
                from Adult CAHtalyst<sup>&reg;</sup> trial at the end of the open-label extension 
                period</span>
            </HcpDrQuote>
            <HcpClinicalInformationAndrogenRelated />
          </>}
          <HcpClinicalInformationDoseReductions index={tabIndex} />
          {tabIndex === 0 && <HcpClinicalInformationReducedGc />}
          {tabIndex === 1 && <HcpDrQuote
            doctor={null}
            theme="lilac"
            className={styles.quote_2}
          >
            I didn&rsquo;t know before this study and starting to take CRENESSITY that taking steroids in high doses had side effects. It feels like [reduced steroid doses] has had big changes.&rdquo;<span className="font-extralight"> — Participant from Adult CAHtalyst<sup>®</sup> trial at the end of the open-label extension period</span>
          </HcpDrQuote>}
          {tabIndex === 1 && <HcpClinicalInformationMajority />}
          {tabIndex === 1 && <HcpClinicalInformationPhysiologic />}
          {tabIndex === 0 && <>
            <HcpClinicalInformationHydrocortisoneDoses />
            <HcpClinicalInformationMetabolicHealth />
            <HcpDrQuote
              className={styles.quote_1}
              theme="lilac"
              doctor={null}
            >
              I feel better [about] the weight loss afforded with the lower steroid dose.”<span
                className="font-extralight"> — Participant from Adult CAHtalyst<sup>®</sup> trial at the end of the open-label extension period</span>
            </HcpDrQuote>
          </>}
          {tabIndex === 1 && <HcpClinicalInformationBMIAndMetabolicHealthData />}
        </>}
        {toggleIndex === 2 && <>
          <HcpClinicalInformationSafety index={tabIndex} />
          <HcpClinicalInformationVideo
            className={styles.init_video}
            heading={<>Initiating CRENESSITY</>}
            description={<>Experts describe how they introduced the option of starting CRENESSITY<br className="mobile--hide"/> and how it could help their patients meet specific treatment goals.</>}
            note={<>These physicians have been compensated by Neurocrine for sharing their views.</>}
            transcript={transcript}
            video={{
              url: "https://player.vimeo.com/progressive_redirect/playback/1132560390/rendition/1080p/file.mp4?loc=external&signature=ad557ce8705c7e9b0f109b547256e17c1f481aa9e381e685982d29d90c167e1c",
              title: "MOA Video",
              poster: "/images/page/hcp/clinical-information/Clinical-Information_CAH-HCP-Specialist-Video-Thumbnail.png",
              caption: "/images/page/hcp/videos/captions/CRENESSITY_INITIATING-CRENESSITY.vtt",
            }}
          />
          <HcpClinicalInformationCompletionRates index={tabIndex} />
          <HcpClinicalInformationDiscontinuation />
          <HcpAccessSimplified/>
        </>}
        <section>
          {tabIndex === 0 && <HcpLongButton
            forceAnchorTag
            iconType="CAHtalyst-Adult"
            label={<span>CAHtalyst<sup>&reg;</sup> Adult</span>}
            description={<span className="inline-block max-w-[400px]">Explore the efficacy and safety data for adult patients.</span>}
            href="/clinical-information#trial-2-efficacy"
            onClick={() => setTimeout(() => window.scrollTo({top: 0, behavior: 'instant'}), 333)}
          />}
          {tabIndex === 1 && <HcpLongButton
            forceAnchorTag
            iconType="person-child2"
            label={<span>CAHtalyst<sup>&reg;</sup> Pediatric</span>}
            description={<span className="inline-block max-w-[400px]">Explore the efficacy and safety data for pediatric patients.</span>}
            href="/clinical-information#trial-1-efficacy-link"
            onClick={() => setTimeout(() => window.scrollTo({top: 0, behavior: 'instant'}), 333)}
          />}
          <HcpLongButton
            iconType="Dosing-Schedule"
            label={<>Dosing</>}
            hideDropShadow
            description={<span className="inline-block max-w-[500px]">Review the dosing and administration information for CRENESSITY.</span>}
            href="/dosing"
          />
          <HcpLongButton
            iconType="Homepage_GC-Dosing-Calculator"
            label={<>GC Dosing Calculators</>}
            hideDropShadow
            description={<span className="inline-block max-w-[588px]">Access these calculators to help navigate GC dosing and down titration when treating with CRENESSITY.</span>}
            href="/dosing#gc-dosing-calculators"
            data-gtm-event-category="Internal Link"
            data-gtm-event-action="Click"
            data-gtm-event-label="GC Dosing Calculators"
          />
          <HcpLongButton
            iconType="Contact-A-Rep"
            label={<>Get in Touch</>}
            description={<span className="inline-block max-w-[600px]">Sign up to schedule a call or visit with a <span className="mobile:text-nowrap">Neurocrine representative.</span></span>}
            href="/support-resources#crm-sign-up"
          />
          <HcpLongButton
            iconType="PeerConnect"
            label={<>PeerConnect</>}
            description={<span className="inline-block max-w-[500px]">Connect with colleagues who treat CAH to consult, collaborate, or transition care.</span>}
            href="/peerconnect"
          />
        </section>
        {tabIndex === 0 && toggleIndex === 0 && <HcpPageFootnote fullWidth>
          17-OHP=17-hydroxyprogesterone; CAH=congenital adrenal hyperplasia; GC=glucocorticoid; SD=standard deviation; 
          TARTs=testicular adrenal rest tumors; ULN=upper limit of normal.
        </HcpPageFootnote>}
        {tabIndex === 0 && toggleIndex === 1 && <HcpPageFootnote fullWidth>
          17-OHP=17-hydroxyprogesterone; CAH=congenital adrenal hyperplasia; CI=confidence interval; GC=glucocorticoid; LSMD=least squares mean difference; ULN=upper limit of normal.
        </HcpPageFootnote>}
        {tabIndex === 0 && toggleIndex === 2 && <HcpPageFootnote fullWidth>
          CAH=congenital adrenal hyperplasia; GC=glucocorticoid.
        </HcpPageFootnote>}
        {tabIndex === 1 && toggleIndex === 0 && <HcpPageFootnote fullWidth>
          17-OHP=17-hydroxyprogesterone; CAH=congenital adrenal hyperplasia; GC=glucocorticoid; SD=standard deviation; 
          TARTs=testicular adrenal rest tumors; ULN=upper limit of normal.
        </HcpPageFootnote>}
        {tabIndex === 1 && toggleIndex === 1 && <HcpPageFootnote fullWidth>
          17-OHP=17-hydroxyprogesterone; ACTH=adrenocorticotropic hormone; ANCOVA=analysis of covariance; 
          BMI=body mass index; CAH=congenital adrenal hyperplasia; GC=glucocorticoid; 
          LSMD=least squares mean difference; ULN=upper limit of normal.
        </HcpPageFootnote>}
        {tabIndex === 1 && toggleIndex === 2 && <HcpPageFootnote fullWidth>
          CAH=congenital adrenal hyperplasia; GC=glucocorticoid.
        </HcpPageFootnote>}
        <HcpPageReferences references={references} />
      </div>
      <Modal ref={patientSatisfactionModalRef} ariaLabelledby="patient-satisfaction-modal-heading" customClass={styles.patient_satisfaction_modal}>
        <HcpClinicalInformationPatientSatisfactionModal />
      </Modal>
    </>
  );
}

HcpClinicalInformation.getLayoutProps = (_pageProps, _router) => ({
  meta,
  hero: (
    <HcpHero
      imageSrc="/images/hero/Clinical-Information_Hero-Image.png"
      theme="lilac"
      imageProps={{
        id: 'hero_kidney_inside_purple',
        width: 630,
        height: 356,
      }}
    >
      <span className="text-hcp-tangerine">CAH</span>NGE ANDROGEN MANAGEMENT
    </HcpHero>
  ),
});